/**
 * Task #4617 — pure helper resolving the custom trip-quote card's
 * host-vs-renter viewer branch.
 *
 * The custom-quote card renders one of two faces:
 *   - the HOST who built the quote sees the enriched `TripQuoteHostCard`
 *     ("You'll receive £X" / "Your earnings"),
 *   - the RENTER it was sent to sees the compact card with the
 *     "Pay & confirm" CTA.
 *
 * The branch must be chosen by the *effective* viewer's message-domain id
 * (`users.id`), never the signed-in account's app-mode. Under admin
 * impersonation the server returns the impersonated user's id as
 * `viewer_id`, so passing that here makes an impersonated renter correctly
 * see the renter card even when the impersonating admin happens to be the
 * car's owner (the reported bug). When not impersonating, `viewerId`
 * equals the admin's own id, so the result is byte-for-byte unchanged.
 *
 * Kept React/i18n-free so the host/renter selection can be pinned with
 * cheap unit tests, mirroring `pastProposalLine.ts`.
 */
export interface QuoteViewerRole {
  /** The viewer is the host/owner who built the quote. */
  viewerIsHost: boolean;
  /** The viewer is the renter the quote was sent to (eligible to pay). */
  viewerIsRenter: boolean;
}

export function resolveQuoteViewerRole(
  viewerId: string | number | null | undefined,
  ownerId: string | number | null | undefined,
  renterId: string | number | null | undefined,
): QuoteViewerRole {
  const v = viewerId != null ? String(viewerId) : null;
  return {
    viewerIsHost: v != null && ownerId != null && String(ownerId) === v,
    viewerIsRenter: v != null && renterId != null && String(renterId) === v,
  };
}
